import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Lock, Key, Users, Building2, RefreshCw, Eye, EyeOff, 
  Copy, Check, Mail, AlertCircle, ChevronDown, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import AppHeader from '@/components/shared/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TherapistLoginManagement = ({ user, onLogout }) => {
  const [properties, setProperties] = useState([]);
  const [sharedLogins, setSharedLogins] = useState({});
  const [therapistsByProperty, setTherapistsByProperty] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedProperties, setExpandedProperties] = useState({});
  
  // Reset password dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState({});
  const [copiedUsername, setCopiedUsername] = useState(null);
  
  // Regenerate PIN dialog
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [regeneratedPin, setRegeneratedPin] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch properties
      const propertiesRes = await axios.get(`${API}/properties`, { headers });
      setProperties(propertiesRes.data);
      
      // Fetch therapists
      const therapistsRes = await axios.get(`${API}/therapists`, { headers });
      
      // Group therapists by property
      const grouped = {};
      therapistsRes.data.forEach(t => {
        const propId = t.assigned_property_id;
        if (!grouped[propId]) grouped[propId] = [];
        grouped[propId].push(t);
      });
      setTherapistsByProperty(grouped);
      
      // Fetch shared login info for each property
      const logins = {};
      for (const prop of propertiesRes.data) {
        try {
          const loginRes = await axios.get(
            `${API}/therapist-auth/property/${prop.hotel_name}/shared-login`, 
            { headers }
          );
          logins[prop.hotel_name] = loginRes.data;
        } catch (err) {
          logins[prop.hotel_name] = { exists: false };
        }
      }
      setSharedLogins(logins);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openResetDialog = (property) => {
    setSelectedProperty(property);
    setNewPassword('');
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/therapist-auth/reset-shared-password`, {
        property_id: selectedProperty.hotel_name,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Password reset for ${selectedProperty.hotel_name}`);
      setResetDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleSetupSharedLogin = async (property) => {
    const username = property.hotel_name.toLowerCase().replace(/\s+/g, '-');
    const defaultPassword = '150395'; // Default password
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/therapist-auth/setup-shared-login`, {
        property_id: property.hotel_name,
        username: username,
        password: defaultPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Shared login created for ${property.hotel_name}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to create shared login');
    }
  };

  const openPinDialog = (therapist) => {
    setSelectedTherapist(therapist);
    setRegeneratedPin(null);
    setPinDialogOpen(true);
  };

  const handleRegeneratePin = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/therapist-auth/generate-pin/${selectedTherapist.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRegeneratedPin(response.data.pin);
      toast.success('New PIN generated');
    } catch (error) {
      toast.error('Failed to regenerate PIN');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedUsername(id);
    setTimeout(() => setCopiedUsername(null), 2000);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader user={user} onLogout={onLogout} showBack={true} backTo="/admin/settings" />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onLogout={onLogout} showBack={true} backTo="/admin/settings" />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-serif text-foreground mb-2">Therapist Login Management</h1>
            <p className="text-muted-foreground">Manage shared logins and PINs for therapists at each spa</p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Properties List */}
        <div className="space-y-4">
          {properties.map((property) => {
            const loginInfo = sharedLogins[property.hotel_name] || { exists: false };
            const therapists = therapistsByProperty[property.hotel_name] || [];
            const isExpanded = expandedProperties[property.hotel_name] || false;
            
            return (
              <Collapsible
                key={property.hotel_name}
                open={isExpanded}
                onOpenChange={(open) => setExpandedProperties(prev => ({
                  ...prev,
                  [property.hotel_name]: open
                }))}
              >
                <Card className="overflow-hidden transition-all duration-200">
                  <CollapsibleTrigger asChild>
                    <CardHeader className="bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          )}
                          <Building2 className="w-6 h-6 text-primary" />
                          <div>
                            <CardTitle className="font-serif">{property.hotel_name}</CardTitle>
                            <CardDescription className="flex items-center gap-2">
                              {property.location}
                              <span className="text-xs">• {therapists.length} therapists</span>
                            </CardDescription>
                          </div>
                        </div>
                        <Badge variant={loginInfo.exists ? 'default' : 'destructive'}>
                          {loginInfo.exists ? 'Login Active' : 'No Login'}
                        </Badge>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="p-6 animate-in slide-in-from-top-2 duration-200">
                      {/* Shared Login Info */}
                      <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <h3 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Shared Login Credentials
                        </h3>
                        
                        {loginInfo.exists ? (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div>
                                <p className="text-sm text-purple-600">Username</p>
                                <div className="flex items-center gap-2">
                                  <code className="text-lg font-mono bg-white px-2 py-1 rounded">
                                    {loginInfo.username}
                                  </code>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(loginInfo.username, property.hotel_name);
                                    }}
                                  >
                                    {copiedUsername === property.hotel_name ? (
                                      <Check className="w-4 h-4 text-green-600" />
                                    ) : (
                                      <Copy className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                              <Button 
                                variant="outline" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openResetDialog(property);
                                }}
                                className="border-purple-300 text-purple-700 hover:bg-purple-100"
                              >
                                <Key className="w-4 h-4 mr-2" />
                                Reset Password
                              </Button>
                            </div>
                            <p className="text-xs text-purple-500">
                              Created: {loginInfo.created_at ? new Date(loginInfo.created_at).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <p className="text-purple-600 flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" />
                              No shared login configured
                            </p>
                            <Button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetupSharedLogin(property);
                              }}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              <Lock className="w-4 h-4 mr-2" />
                              Create Shared Login
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* Therapists List */}
                      <div>
                        <h3 className="font-medium mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          Therapists ({therapists.length})
                        </h3>
                        
                        {therapists.length === 0 ? (
                          <p className="text-muted-foreground text-sm py-4 text-center bg-muted/30 rounded-lg">
                            No therapists assigned to this property
                          </p>
                        ) : (
                          <div className="grid gap-2">
                            {therapists.map((therapist) => (
                              <div 
                                key={therapist.id || therapist.user_id} 
                                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <span className="text-xs font-medium text-primary">
                                      {therapist.full_name?.charAt(0)}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="font-medium">{therapist.full_name}</p>
                                    <p className="text-xs text-muted-foreground">{therapist.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="text-right">
                                    <p className="text-xs text-muted-foreground">PIN</p>
                                    <div className="flex items-center gap-1">
                                      <code className="text-sm font-mono bg-white px-2 py-0.5 rounded border">
                                        {showPassword[therapist.id || therapist.user_id] ? therapist.pin : '••••'}
                                      </code>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const key = therapist.id || therapist.user_id;
                                          setShowPassword(prev => ({
                                            ...prev,
                                            [key]: !prev[key]
                                          }));
                                        }}
                                      >
                                        {showPassword[therapist.id || therapist.user_id] ? (
                                          <EyeOff className="w-3 h-3" />
                                        ) : (
                                          <Eye className="w-3 h-3" />
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openPinDialog({...therapist, id: therapist.id || therapist.user_id});
                                    }}
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    New PIN
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">Reset Shared Password</DialogTitle>
            <DialogDescription>
              Reset the shared login password for {selectedProperty?.hotel_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                All therapists at this spa will need to use the new password to log in.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} className="bg-purple-600 hover:bg-purple-700">
              Reset Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Regenerate PIN Dialog */}
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">Regenerate PIN</DialogTitle>
            <DialogDescription>
              Generate a new PIN for {selectedTherapist?.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {regeneratedPin ? (
              <div className="text-center p-6 bg-green-50 rounded-lg border border-green-200">
                <Check className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <p className="text-sm text-green-700 mb-2">New PIN Generated:</p>
                <code className="text-3xl font-mono font-bold text-green-800 tracking-widest">
                  {regeneratedPin}
                </code>
                <p className="text-xs text-green-600 mt-3">
                  Please share this PIN with {selectedTherapist?.full_name}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => copyToClipboard(regeneratedPin, 'pin')}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy PIN
                </Button>
              </div>
            ) : (
              <div className="text-center p-6">
                <p className="text-muted-foreground mb-4">
                  This will generate a new 4-digit PIN and invalidate the old one.
                </p>
                <p className="text-sm text-amber-600 mb-4">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  The therapist will need to use the new PIN to access operations.
                </p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setPinDialogOpen(false)}>
              {regeneratedPin ? 'Close' : 'Cancel'}
            </Button>
            {!regeneratedPin && (
              <Button onClick={handleRegeneratePin} className="bg-purple-600 hover:bg-purple-700">
                <RefreshCw className="w-4 h-4 mr-2" />
                Generate New PIN
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TherapistLoginManagement;
